import { createColumnHelper, flexRender, useTable } from '@tanstack/react-table';
import type { OnChangeFn, Row, RowData, RowSelectionState } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Download } from 'lucide-react';
import type { JSX, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Pagination } from '@/components/ui/pagination.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { cn } from '@/lib/cn.js';
import type { DataTableColumnDef, DataTableFeatures } from './features.js';
import { dataTableFeatures } from './features.js';
import { useMediaQuery } from './use-media-query.js';
import type { TableSortDir } from './use-table-search.js';

export type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';

export interface DataTableEmptyState {
  title: string;
  description?: string;
  action?: ReactNode;
}

export interface DataTableProps<TData extends RowData> {
  columns: DataTableColumnDef<TData>[];
  data: TData[];
  getRowId: (row: TData) => string;
  /** Total de la liste filtrée côté API (`Paginated<T>.total`), pas `data.length`. */
  total: number;
  page: number;
  pageSize: number;
  sortBy?: string | undefined;
  sortDir?: TableSortDir | undefined;
  /** Identifiants de colonnes triables (doivent correspondre au `sortBy` attendu par l'API). */
  sortableColumnIds?: readonly string[];
  /**
   * Colonnes numériques (montants, quantités, compteurs) : alignées à droite,
   * chiffres tabulaires. La colonne `actions` est toujours alignée à droite.
   */
  numericColumnIds?: readonly string[];
  onPageChange: (page: number) => void;
  onSortChange?: (sortBy: string, sortDir: TableSortDir) => void;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  errorDetail?: string | undefined;
  onRetry?: (() => void) | undefined;
  onRowClick?: (row: TData) => void;
  emptyState: DataTableEmptyState;
  /** Filtres de l'écran (champs, sélecteurs…) — l'écran les synchronise lui-même à l'URL. */
  toolbar?: ReactNode;
  /**
   * Lignes exclues de la sélection multiple (cases à cocher désactivées et
   * ignorées par « tout sélectionner »). Sans cette prop, toutes les lignes
   * sont sélectionnables.
   */
  isRowSelectable?: (row: TData) => boolean;
  /** `02` §0 : « export CSV de la liste filtrée ». Absent = pas de bouton. */
  onExportCsv?: () => void;
  caption?: string;
  /**
   * Sélection multiple de lignes (`02` §3 : Commandes, « Envoyer maintenant »,
   * « Régénérer », « Marquer ignorée »). **Opt-in** : fournir les deux props
   * ajoute une colonne de cases à cocher en tête de tableau ; les omettre (le
   * cas de tous les autres écrans) laisse `DataTable` strictement inchangé.
   * `rowSelection` est tenu par l'écran appelant (`useState`), comme
   * `state.rowSelection` + `onRowSelectionChange` de la table-state skill
   * v9 — jamais les deux à moitié (pas de `rowSelection` sans le callback).
   * Les clés sont les `id` renvoyés par `getRowId`, pas l'index de ligne.
   */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}

const SKELETON_ROWS = 5;

/**
 * Vrai si le clic vient réellement de la ligne (ou de la carte) dans le DOM.
 * React fait remonter les événements le long de l'arbre des composants, portails
 * compris : sans ce garde, un clic dans le menu d'actions d'une ligne ou dans le
 * dialogue qu'il ouvre (rendus dans `document.body`) déclencherait aussi
 * `onRowClick` et quitterait la page.
 */
function isEventFromElement(event: MouseEvent<HTMLElement>): boolean {
  return event.target instanceof Node && event.currentTarget.contains(event.target);
}
/** Identifiant de colonne conventionnel des actions de ligne — aligné à droite. */
const ACTIONS_COLUMN_ID = 'actions';
/** Tailwind `md` — sous ce seuil, `DataTable` rend des cartes plutôt qu'un tableau (`README-shell.md`). */
const CARD_BREAKPOINT_QUERY = '(min-width: 768px)';

function nextSortDir(
  columnId: string,
  sortBy: string | undefined,
  sortDir: TableSortDir | undefined,
): TableSortDir {
  if (sortBy !== columnId) {
    return 'asc';
  }
  return sortDir === 'asc' ? 'desc' : 'asc';
}

/**
 * Libellé de carte dérivé du header de colonne existant — jamais de config
 * dédiée par écran. Un header vide (`''`, colonne actions) ou une fonction
 * (`header: ({table}) => …`, colonne de sélection ci-dessous) n'a pas de
 * libellé exploitable : la cellule est alors rendue seule, sans étiquette.
 */
function getColumnHeaderLabel(header: unknown): string | null {
  if (typeof header !== 'string') {
    return null;
  }
  const trimmed = header.trim();
  return trimmed.length > 0 ? trimmed : null;
}

interface DataTableCardRowProps<TData extends RowData> {
  row: Row<DataTableFeatures, TData>;
  onRowClick?: ((row: TData) => void) | undefined;
}

/**
 * Équivalent carte d'une `<TableRow>` sous 768px (`README-shell.md` :
 * « tableaux qui passent en cartes sur petit écran »). Une ligne par colonne
 * visible, libellé + valeur rendue par le même `cell` que la version tableau.
 *
 * `onRowClick` se déclenche au clic n'importe où sur la carte (comme
 * `<TableRow onClick>`) et, en plus, au clavier via Entrée — condition
 * `event.target === event.currentTarget` pour ignorer les Entrée qui
 * bouillonnent depuis un contrôle imbriqué (case à cocher, bouton d'action) :
 * ces contrôles gèrent déjà leur propre activation et coupent la
 * propagation de leur `onClick` (voir `selectColumn` plus bas).
 */
function DataTableCardRow<TData extends RowData>({
  row,
  onRowClick,
}: DataTableCardRowProps<TData>): JSX.Element {
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (!onRowClick || event.target !== event.currentTarget || event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    onRowClick(row.original);
  }

  return (
    <div
      role="listitem"
      tabIndex={onRowClick ? 0 : undefined}
      onClick={
        onRowClick
          ? (event) => {
              if (isEventFromElement(event)) {
                onRowClick(row.original);
              }
            }
          : undefined
      }
      onKeyDown={onRowClick ? handleKeyDown : undefined}
      className={cn(
        'flex flex-col gap-2 px-4 py-3 data-[state=selected]:bg-selection',
        onRowClick &&
          'cursor-pointer transition-colors hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
      )}
      data-state={row.getIsSelected() ? 'selected' : undefined}
    >
      {row.getAllCells().map((cell) => {
        const label = getColumnHeaderLabel(cell.column.columnDef.header);
        const content = flexRender(cell.column.columnDef.cell, cell.getContext());

        if (label === null) {
          return <div key={cell.id}>{content}</div>;
        }

        return (
          <div
            key={cell.id}
            className="grid min-h-6 grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-3 text-sm"
          >
            <span className="pt-0.5 text-xs text-muted-foreground">{label}</span>
            <div className="min-w-0 break-words [&>*]:max-w-full">{content}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Tableau générique réutilisé par tous les écrans de liste (`02` §0) : tri,
 * pagination 50 lignes et filtres reflétés dans l'URL (voir
 * `use-table-search.ts`), export CSV de la liste filtrée. Tri/pagination/
 * filtres sont **toujours** pilotés par le serveur — `DataTable` ne recalcule
 * rien côté client, il affiche `data`/`total` tels que renvoyés par l'API.
 */
export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  total,
  page,
  pageSize,
  sortBy,
  sortDir,
  sortableColumnIds = [],
  numericColumnIds = [],
  onPageChange,
  onSortChange,
  isLoading = false,
  isError = false,
  errorMessage = 'Impossible de charger cette liste.',
  errorDetail,
  onRetry,
  onRowClick,
  emptyState,
  toolbar,
  isRowSelectable,
  onExportCsv,
  caption,
  rowSelection,
  onRowSelectionChange,
}: DataTableProps<TData>): JSX.Element {
  const enableRowSelection = rowSelection !== undefined && onRowSelectionChange !== undefined;

  const selectColumn = useMemo<DataTableColumnDef<TData>>(() => {
    const helper = createColumnHelper<DataTableFeatures, TData>();
    return helper.display({
      id: '__select',
      header: ({ table: headerTable }) => (
        <Checkbox
          checked={
            headerTable.getIsAllRowsSelected()
              ? true
              : headerTable.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(checked) => {
            headerTable.toggleAllRowsSelected(checked === true);
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
          aria-label="Sélectionner toutes les lignes de cette page"
        />
      ),
      cell: ({ row }) =>
        isRowSelectable && !isRowSelectable(row.original) ? null : (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => {
              row.toggleSelected(checked === true);
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
            aria-label="Sélectionner cette ligne"
          />
        ),
    });
  }, [isRowSelectable]);

  const effectiveColumns = useMemo(
    () => (enableRowSelection ? [selectColumn, ...columns] : columns),
    [enableRowSelection, selectColumn, columns],
  );

  const noopRowSelectionChange: OnChangeFn<RowSelectionState> = () => {
    // Sélection non fournie par l'écran (opt-in) : aucune case rendue, cet
    // état interne n'est donc jamais atteint — présent uniquement pour garder
    // `useTable` inconditionnellement contrôlé (types stables, pas de branche
    // conditionnelle sur la forme des options).
  };

  const table = useTable<DataTableFeatures, TData>({
    features: dataTableFeatures,
    columns: effectiveColumns,
    data,
    getRowId: (row) => getRowId(row),
    state: { rowSelection: rowSelection ?? {} },
    onRowSelectionChange: onRowSelectionChange ?? noopRowSelectionChange,
    enableRowSelection: isRowSelectable ? (row) => isRowSelectable(row.original) : true,
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const isEmpty = !isLoading && !isError && data.length === 0;
  const isDesktop = useMediaQuery(CARD_BREAKPOINT_QUERY);

  function alignClass(columnId: string): string | undefined {
    if (numericColumnIds.includes(columnId)) {
      return 'text-right tabular-nums';
    }
    return columnId === ACTIONS_COLUMN_ID ? 'text-right' : undefined;
  }

  function handleRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: TData): void {
    // Même règle que les cartes : seule la ligne elle-même, pas un contrôle imbriqué.
    if (!onRowClick || event.target !== event.currentTarget || event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    onRowClick(row);
  }

  return (
    <div className="flex flex-col gap-3">
      {toolbar || onExportCsv ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          {toolbar ? <div className="min-w-0 flex-1">{toolbar}</div> : null}
          {onExportCsv ? (
            <Button
              type="button"
              variant="outline"
              className="self-end sm:self-start"
              onClick={onExportCsv}
            >
              <Download aria-hidden="true" />
              Exporter CSV
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {isDesktop ? (
          <Table>
            {caption ? <TableCaption className="sr-only">{caption}</TableCaption> : null}
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSortable = sortableColumnIds.includes(header.column.id);
                    const isActive = isSortable && sortBy === header.column.id;
                    const ariaSort = isActive
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none';
                    const content = header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext());

                    return (
                      <TableHead
                        key={header.id}
                        aria-sort={isSortable ? ariaSort : undefined}
                        className={alignClass(header.column.id)}
                      >
                        {isSortable ? (
                          <button
                            type="button"
                            className={cn(
                              '-mx-1.5 inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md px-1.5 text-left font-medium transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              isActive && 'text-foreground',
                            )}
                            onClick={() => {
                              onSortChange?.(
                                header.column.id,
                                nextSortDir(header.column.id, sortBy, sortDir),
                              );
                            }}
                          >
                            {content}
                            {isActive ? (
                              sortDir === 'asc' ? (
                                <ArrowUp className="size-3.5" aria-hidden="true" />
                              ) : (
                                <ArrowDown className="size-3.5" aria-hidden="true" />
                              )
                            ) : (
                              <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          content
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                    <TableRow key={`skeleton-${String(rowIndex)}`}>
                      {effectiveColumns.map((_column, columnIndex) => (
                        <TableCell key={`skeleton-cell-${String(columnIndex)}`}>
                          <Skeleton className="h-3.5 w-full max-w-40" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      onClick={
                        onRowClick
                          ? (event) => {
                              if (isEventFromElement(event)) {
                                onRowClick(row.original);
                              }
                            }
                          : undefined
                      }
                      onKeyDown={
                        onRowClick ? (event) => handleRowKeyDown(event, row.original) : undefined
                      }
                      className={
                        onRowClick
                          ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
                          : undefined
                      }
                    >
                      {row.getAllCells().map((cell) => (
                        <TableCell key={cell.id} className={alignClass(cell.column.id)}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        ) : (
          <div>
            {enableRowSelection ? (
              <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
                <Checkbox
                  checked={
                    table.getIsAllRowsSelected()
                      ? true
                      : table.getIsSomeRowsSelected()
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(checked) => {
                    table.toggleAllRowsSelected(checked === true);
                  }}
                  aria-label="Sélectionner toutes les lignes de cette page"
                />
                <span className="text-xs text-muted-foreground">Tout sélectionner</span>
              </div>
            ) : null}
            <div className="divide-y divide-border" role="list" aria-label={caption}>
              {isLoading
                ? Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                    <div
                      key={`skeleton-card-${String(rowIndex)}`}
                      className="flex flex-col gap-2 px-4 py-3"
                    >
                      {effectiveColumns.map((_column, columnIndex) => (
                        <Skeleton
                          key={`skeleton-card-cell-${String(columnIndex)}`}
                          className="h-3.5 w-full"
                        />
                      ))}
                    </div>
                  ))
                : table
                    .getRowModel()
                    .rows.map((row) => (
                      <DataTableCardRow key={row.id} row={row} onRowClick={onRowClick} />
                    ))}
            </div>
          </div>
        )}

        {isError ? (
          <ErrorState
            message={errorMessage}
            detail={errorDetail}
            onRetry={onRetry}
            className="rounded-none border-0 border-t border-border first:border-t-0"
          />
        ) : null}
        {isEmpty ? (
          <EmptyState
            title={emptyState.title}
            description={emptyState.description}
            action={emptyState.action}
          />
        ) : null}
      </div>

      {!isError && !isEmpty ? (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="text-xs tabular-nums text-muted-foreground">
            {total} résultat{total > 1 ? 's' : ''}
          </p>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            disabled={isLoading}
          />
        </div>
      ) : null}
    </div>
  );
}

export { createDataTableColumnHelper } from './features.js';
export type { DataTableColumnDef } from './features.js';
export { useDataTableUrlState } from './use-table-search.js';
export type { DataTableUrlState, TableSortDir } from './use-table-search.js';
